<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { CHAT_QUICK_REACTIONS, CHAT_REACTIONS } from '@/modules/chat-matrix-fast/constants/reactions'
import { CHAT_FLOATING_Z_INDEX } from '@/modules/chat-matrix-fast/constants/ui'
import { adjustElementPosition } from '../../chatModules'

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

interface Props {
  modelValue: boolean
  position: { top: number; left: number } | null
  activeEmoji?: string | null
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'select-emoji', emoji: string): void
  (e: 'mouseenter'): void
  (e: 'mouseleave'): void
}

const pickerRef = ref<HTMLElement>()
const showFullList = ref(false)

watch(
  () => props.modelValue,
  open => {
    if (!open) {
      showFullList.value = false
    }
  },
)

const adjustedPosition = computed(() => {
  if (!props.position || !pickerRef.value) {
    return props.position
  }

  return adjustElementPosition(pickerRef.value, props.position)
})

const normalizeEmoji = (emoji: string) => emoji.replace(/\uFE0F/g, '').trim()

const isActive = (emoji: string) => {
  if (!props.activeEmoji) {
    return false
  }
  return normalizeEmoji(props.activeEmoji) === normalizeEmoji(emoji)
}

const handleEmojiSelect = (emoji: string) => {
  emit('select-emoji', emoji)
  // Не закрываем здесь — родитель закроет после успешного apply
}

const toggleFullList = () => {
  showFullList.value = !showFullList.value
}
</script>

<template>
  <Teleport to="body">
    <Transition name="reaction-picker-fade">
      <div
        v-show="modelValue && position"
        ref="pickerRef"
        class="reaction-picker"
        :class="{ 'reaction-picker--expanded': showFullList }"
        :style="position ? {
          position: 'fixed',
          top: `${(adjustedPosition?.top ?? position.top)}px`,
          left: `${(adjustedPosition?.left ?? position.left)}px`,
          transform: 'translate(-50%, -100%)',
          zIndex: CHAT_FLOATING_Z_INDEX,
        } : undefined"
        @mouseenter="$emit('mouseenter')"
        @mouseleave="$emit('mouseleave')"
      >
        <div
          v-if="!showFullList"
          class="reaction-picker-strip"
        >
          <button
            v-for="reaction in CHAT_QUICK_REACTIONS"
            :key="reaction.emoji"
            class="reaction-picker-item"
            :class="{ 'reaction-picker-item--active': isActive(reaction.emoji) }"
            type="button"
            :title="reaction.name"
            @click="handleEmojiSelect(reaction.emoji)"
          >
            <span class="reaction-picker-emoji">{{ reaction.emoji }}</span>
          </button>

          <button
            class="reaction-picker-item reaction-picker-expand"
            type="button"
            aria-label="Открыть все реакции"
            title="Ещё"
            @click.stop="toggleFullList"
          >
            <VIcon
              icon="ri-arrow-down-s-line"
              size="22"
            />
          </button>
        </div>

        <div
          v-else
          class="reaction-picker-grid-wrap"
          @click.stop
        >
          <div class="reaction-picker-grid">
            <button
              v-for="reaction in CHAT_REACTIONS"
              :key="reaction.emoji"
              class="reaction-picker-item"
              :class="{ 'reaction-picker-item--active': isActive(reaction.emoji) }"
              type="button"
              :title="reaction.name"
              @click="handleEmojiSelect(reaction.emoji)"
            >
              <span class="reaction-picker-emoji">{{ reaction.emoji }}</span>
            </button>
          </div>
          <button
            class="reaction-picker-collapse"
            type="button"
            aria-label="Свернуть"
            @click.stop="toggleFullList"
          >
            <VIcon
              icon="ri-arrow-up-s-line"
              size="20"
            />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.reaction-picker {
  background: #fff;
  border-radius: 28px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.16);
  padding: 6px 8px;
}

.reaction-picker--expanded {
  border-radius: 16px;
  padding: 10px 8px 6px;
  width: min(292px, calc(100vw - 24px));
}

.reaction-picker-strip {
  display: flex;
  gap: 2px;
  align-items: center;
}

.reaction-picker-grid-wrap {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.reaction-picker-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  max-height: min(280px, 45vh);
  overflow-y: auto;
  padding: 2px;
}

.reaction-picker-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.15s;
  margin: 0 auto;
}

.reaction-picker-item:hover {
  background-color: rgba(0, 0, 0, 0.06);
  transform: scale(1.12);
}

.reaction-picker-item--active {
  background-color: #2fc6f6;
}

.reaction-picker-item--active:hover {
  background-color: #1bb5e5;
}

.reaction-picker-expand {
  color: rgba(0, 0, 0, 0.45);
  margin-left: 2px;
}

.reaction-picker-expand:hover {
  color: rgba(0, 0, 0, 0.7);
}

.reaction-picker-collapse {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 28px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  border-radius: 8px;
}

.reaction-picker-collapse:hover {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.65);
}

.reaction-picker-emoji {
  font-size: 22px;
  line-height: 1;
}

.reaction-picker-fade-enter-active,
.reaction-picker-fade-leave-active {
  transition: opacity 0.1s ease;
}

.reaction-picker-fade-enter-from,
.reaction-picker-fade-leave-to {
  opacity: 0;
}
</style>
